import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { ApiService } from "src/app/shared/services/api.service";
import { PermissionService } from "src/app/shared/services/permission.service";
import { IHasData, ISkippedAndDuplicateSamples } from "../interfaces/rna-mutations.interface";

@Injectable({
  providedIn: "root",
})
export class RnaMutationsService {
  constructor(
    private api: ApiService,
    private permissionService: PermissionService
  ) {}

  /**
   * Whether the user's role allows uploading RNA-seq and mutations (see uploadRNA() and uploadMutation()).
   * The backend grants every upload (metadata, RNA-seq, mutations) through the single "/upload" route.
   * @return True if uploading RNA-seq and mutations is allowed, false otherwise
   */
  public canUpload(): boolean {
    return this.permissionService.isRouteEnabled(Functionality.IMPORT_DATA, "POST", "/upload");
  }

  /**
   * Upload rna.
   * @param formData The formdata containing the rna
   * @param biobankID The biobank identifier
   * @param sampleID The sample identifier
   * @return The sample name skipped and the duplicates genes
   */
  public uploadRNA(file: FormData, biobankID: number, sampleID?: string): Observable<ISkippedAndDuplicateSamples> {
    const params: Record<string, string | number> = {
      biobankID: biobankID,
    };

    if (sampleID) {
      params["sampleID"] = sampleID;
    }

    return this.api.post("upload/rna", file, undefined, true, params) as Observable<ISkippedAndDuplicateSamples>;
  }

  /**
   * Upload mutation.
   * @param formData The formdata containing the mutation
   * @param biobankID The biobank identifier
   * @return The sample name skipped and the duplicates genes
   */
  public uploadMutation(formData: FormData, biobankID: number): Observable<ISkippedAndDuplicateSamples> {
    return this.api.post(`upload/mutation`, formData, undefined, true, { biobankID: biobankID }) as Observable<ISkippedAndDuplicateSamples>;
  }

  /**
   * Check if a sample has an RNA mutation for a given biobank
   * @param biobankID The biobank identifier
   * @param sampleID The sample identifier
   * @return The API response indicating if an RNA mutation exists
   */
  public getHasRnaMutation(biobankID: number, sampleID: string): Observable<IHasData> {
    return this.api.get("hasRnaMutation", {
      biobankID: biobankID,
      sampleID: sampleID,
    }) as Observable<IHasData>;
  }
}
